import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";
import { Photo, type PhotoName } from "./photo";

/**
 * An open content section, separated by a rule and a titled header.
 */
export function Panel({ title, description, action, children, className, bodyClassName }: { title?: ReactNode; description?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={cn("ui-section min-w-0", className)}>
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-4 pb-5 pt-5">
          <div className="min-w-0">
            {title && (
              <h2 className="flex items-center gap-2 font-display text-[1.03rem] font-bold text-ink">
                <span className="min-w-0">{title}</span>
              </h2>
            )}
            {description && <p className="mt-0.5 text-[13px] leading-relaxed text-muted">{description}</p>}
          </div>
          {action && <div className="flex shrink-0 flex-wrap gap-2">{action}</div>}
        </header>
      )}
      <div className={cn(title || action ? "pb-6" : "py-6", bodyClassName)}>{children}</div>
    </section>
  );
}

/** Section heading with the brand underline used inside panels and pages. */
export function SectionTitle({ children, className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn("mb-3 border-b border-line-strong pb-1.5 font-display text-[15px] font-bold text-ink", className)} {...props}>
      {children}
    </h3>
  );
}

/** `photo`: for a screen with nothing in it yet, a small framed photo in place of the icon. */
export function EmptyState({ icon, photo, title, children, action }: { icon?: ReactNode; photo?: PhotoName; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      {photo ? (
        <div className="mb-5 w-44 sm:w-52">
          <div className="relative aspect-[4/3] overflow-hidden rounded">
            <Photo name={photo} fill sizes="13rem" decorative />
          </div>
        </div>
      ) : icon && (
        // An ink ring (円相) around the icon.
        <div className="relative mb-4 flex size-16 items-center justify-center text-brand [&>svg:last-child]:size-6">
          <svg viewBox="0 0 64 64" className="absolute inset-0 size-16" aria-hidden>
            <path d="M50 14 A 24 24 0 1 0 54 26" fill="none" stroke="currentColor" strokeOpacity="0.28" strokeWidth="5" strokeLinecap="round" />
          </svg>
          {icon}
        </div>
      )}
      <p className="font-display text-[1.05rem] font-bold text-ink">{title}</p>
      {children && <div className="mt-1 max-w-md text-sm text-muted">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton rounded-md", className)} />;
}

export function Spinner({ label = "読み込み中" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-muted" role="status">
      <InkDrop className="size-7" />
      {label}
    </div>
  );
}

/** A drop of ink spreading into paper — the waiting mark. */
export function InkDrop({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={cn("text-brand", className)} aria-hidden>
      <circle cx="20" cy="20" r="9" fill="currentColor" className="animate-ink" />
      <circle cx="20" cy="20" r="9" fill="currentColor" className="animate-ink" style={{ animationDelay: "0.8s" }} />
      <circle cx="20" cy="20" r="9" fill="currentColor" className="animate-ink" style={{ animationDelay: "1.6s" }} />
      <circle cx="20" cy="20" r="4.5" fill="currentColor" />
    </svg>
  );
}
